import appdata from '../Services/appdata';

export const API_ROOT = `${window.location.origin}/api`;
export const API_ASYNC_POLL_TIMEOUT = 1000;
export const API_ASYNC_POLL_COUNT = 700;
export const API_ASYNC_LONG_POLL_TIMEOUT = 1500;
export const API_ASYNC_LONG_POLL_COUNT = 1200; // 30 minutes for long-running integration refreshes

const AUTH_EXPIRED_MESSAGE = 'Your R4D login has expired. Please sign in again.';
const AUTH_RENEW_BEFORE_MS = 2 * 60 * 1000;
const AUTH_ACTIVE_WITHIN_MS = 8 * 60 * 1000;
let refreshPromise = null;
let authExpiryHandled = false;

function buildHeaders(username_or_token, password, extra = {}) {
  const token = appdata.getItem('token');
  const credential = username_or_token || token;
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...extra,
  };
  if (credential) {
    headers.Authorization = `Basic ${btoa(`${credential}:${password || ''}`)}`;
  }
  return headers;
}

// URLSafeTimedSerializer tokens contain a signed JSON payload with an exp
// timestamp. Decode exp only for refresh scheduling; Flask validates the
// signature and actual expiry on every authenticated request.
export function tokenExpiresAt(token) {
  if (!token || typeof token !== 'string') return null;
  try {
    const part = token.split('.')[0];
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')));
    return Number.isFinite(Number(payload.exp)) ? Number(payload.exp) * 1000 : null;
  } catch (_) {
    return null;
  }
}

function isPublicAuthRoute(url) {
  // Password-based sign-in, registration and recovery must continue working
  // even when a previous login has expired.
  return /\/api\/auth\/(login|register|reset_password(?:_email)?)(?:\?|\/|$)/.test(String(url));
}

function expireSession() {
  if (authExpiryHandled) return;
  authExpiryHandled = true;
  // Remove stale credentials before navigation. One redirect also stops the
  // dozens of independent charts from producing duplicate expiry toasts.
  sessionStorage.clear();
  window.location.replace('/login?session=expired');
}

// All API requests share this promise. Without single-flight renewal, a page
// containing 20 charts could race 20 token refreshes and overwrite each other.
async function renewAuthToken() {
  const current = appdata.getItem('token');
  if (!current) return { ok: false, reason: 'missing' };
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const expiresAt = tokenExpiresAt(current);
    if (expiresAt !== null && expiresAt <= Date.now()) {
      return { ok: false, reason: 'expired' };
    }
    try {
      const response = await fetch(`${API_ROOT}/auth/token/refresh`, {
        method: 'POST',
        cache: 'no-store',
        headers: buildHeaders(current),
      });
      if (response.status === 401) return { ok: false, reason: 'expired' };
      if (!response.ok) return { ok: false, reason: 'network' };
      const data = await response.json();
      if (data.status !== 200 || !data.token) return { ok: false, reason: 'network' };
      // Do not overwrite a newer login (or a sign-out) with an old refresh.
      if (appdata.getItem('token') === current) {
        appdata.setItem('token', data.token);
      }
      return { ok: !!appdata.getItem('token'), reason: 'ok' };
    } catch (error) {
      console.warn('Could not renew R4D login token:', error);
      return { ok: false, reason: 'network' };
    }
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

// Used by activity-aware keepalive and by long-running Celery poll loops.
// A temporary network failure is not treated as a revoked login.
export async function ensureAuthFresh(force = false) {
  if (authExpiryHandled) return false;
  const token = appdata.getItem('token');
  if (!token) return false;
  const expiresAt = tokenExpiresAt(token);
  if (expiresAt !== null && expiresAt <= Date.now()) {
    expireSession();
    return false;
  }
  if (!force && expiresAt !== null && expiresAt - Date.now() > AUTH_RENEW_BEFORE_MS) {
    return true;
  }
  const result = await renewAuthToken();
  if (!result.ok && result.reason === 'expired') {
    expireSession();
    return false;
  }
  // Still-valid credentials may be used despite a temporary refresh failure.
  return result.ok || (expiresAt !== null && expiresAt > Date.now());
}

// Keep a genuinely active reporting session alive, even if the user is viewing
// a chart without making API requests. Hidden or idle tabs do not refresh
// indefinitely. Every new request also checks expiry, irrespective of timer.
export function installAuthKeepAlive() {
  let lastActivity = Date.now();
  const noteActivity = () => { lastActivity = Date.now(); };
  const tick = () => {
    if (!document.hidden && appdata.getItem('token') &&
        Date.now() - lastActivity < AUTH_ACTIVE_WITHIN_MS) {
      void ensureAuthFresh();
    }
  };
  const interval = setInterval(tick, 30000);
  window.addEventListener('pointerdown', noteActivity, { passive: true });
  window.addEventListener('keydown', noteActivity);
  window.addEventListener('wheel', noteActivity, { passive: true });
  window.addEventListener('focus', noteActivity);
  // Returning to a tab should check the token immediately, not wait 30s.
  const onVisibilityChange = () => {
    if (!document.hidden) { noteActivity(); tick(); }
  };
  document.addEventListener('visibilitychange', onVisibilityChange);
  return () => {
    clearInterval(interval);
    window.removeEventListener('pointerdown', noteActivity);
    window.removeEventListener('keydown', noteActivity);
    window.removeEventListener('wheel', noteActivity);
    window.removeEventListener('focus', noteActivity);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
}

// Shared authenticated fetch for report requests, file downloads and direct
// management endpoints. Retry once with a fresh token if an in-flight request
// used an old token. Never retry an unsafe POST after a response other than 401.
export async function fetchWithSession(url, options = {}, username_or_token, password) {
  // Requests already in flight must not continue anonymously after a logout.
  // Login and password recovery remain available.
  if (authExpiryHandled && !isPublicAuthRoute(url)) {
    throw new Error(AUTH_EXPIRED_MESSAGE);
  }
  const explicitCredential = !!username_or_token;
  const signedIn = !!appdata.getItem('token');
  const isPublic = isPublicAuthRoute(url);
  const user = appdata.getItem('user');
  if (!signedIn && !isPublic && user && !user.is_admin && !explicitCredential) {
    expireSession();
    throw new Error(AUTH_EXPIRED_MESSAGE);
  }
  const useSession = signedIn && !explicitCredential && !isPublic;

  if (useSession) {
    const allowed = await ensureAuthFresh();
    if (!allowed && authExpiryHandled) {
      throw new Error(AUTH_EXPIRED_MESSAGE);
    }
  }
  const send = () => fetch(url, {
    ...options,
    headers: buildHeaders(username_or_token, password, options.headers || {}),
  });
  const tokenBefore = appdata.getItem('token');
  let response = await send();
  if (response.status !== 401 || !useSession) return response;

  const tokenNow = appdata.getItem('token');
  let canRetry = !!tokenNow && tokenBefore !== tokenNow;
  if (!canRetry) {
    const result = await renewAuthToken();
    canRetry = result.ok;
  }
  if (canRetry) response = await send();
  if (response.status === 401 || !canRetry) expireSession();
  return response;
}

function failedRequest(response, verb) {
  return {
    status: response.status,
    error: response.status === 401 ? AUTH_EXPIRED_MESSAGE : `Failed to ${verb} the request (HTTP ${response.status}).`,
  };
}

export async function dispatchGetJSONAsync(url, username_or_token, password) {
  try {
    const requestUrl = `${url}${url.includes('?') ? '&' : '?'}_ts=${Date.now()}`;
    const response = await fetchWithSession(requestUrl, {
      method: 'GET',
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
      },
    }, username_or_token, password);
    if (!response.ok) return failedRequest(response, 'start');
    return await response.json();
  } catch (error) {
    if (!authExpiryHandled) console.error('API GET error', error);
    return { status: authExpiryHandled ? 401 : 503,
      error: authExpiryHandled ? AUTH_EXPIRED_MESSAGE : 'Unable to reach the R4D server.' };
  }
}

export async function dispatchPostJSONAsync(url, json, username_or_token, password) {
  try {
    const response = await fetchWithSession(url, {
      method: 'POST',
      body: JSON.stringify(json),
    }, username_or_token, password);
    if (!response.ok) return failedRequest(response, 'start');
    return await response.json();
  } catch (error) {
    if (!authExpiryHandled) console.error('API POST error', error);
    return { status: authExpiryHandled ? 401 : 503,
      error: authExpiryHandled ? AUTH_EXPIRED_MESSAGE : 'Unable to reach the R4D server.' };
  }
}

export async function completeJSONAsync(task_data, username_or_token, password, pollOptions = {}) {
  if (!task_data?.task_name || !task_data?.task_id) {
    return {
      status: task_data?.status || 500,
      message: task_data?.error || 'The background task could not be started.',
    };
  }

  try {
    const url = `${API_ROOT}/broker/task/${task_data.task_name}/${task_data.task_id}`;
    const customPollTimeout = pollOptions.pollTimeout;
    const pollTimeout = customPollTimeout ?? API_ASYNC_POLL_TIMEOUT;
    const pollCount = pollOptions.pollCount ?? API_ASYNC_POLL_COUNT;
    const fastPollSchedule = [75, 150, 300, 600, 1000];
    let elapsedMs = 0;
    let nextAuthRenewalMs = 0;

    for (let i = 0; i < pollCount; ++i) {
      const delay = customPollTimeout !== undefined ? pollTimeout : (fastPollSchedule[i] ?? API_ASYNC_POLL_TIMEOUT);
      await new Promise((r) => setTimeout(r, delay));
      elapsedMs += delay;
      if (customPollTimeout !== undefined && elapsedMs >= nextAuthRenewalMs && appdata.getItem('token')) {
        // Long refreshes explicitly extend the session while the job is polled.
        await ensureAuthFresh(true);
        if (authExpiryHandled) {
          return { status: 401, message: AUTH_EXPIRED_MESSAGE };
        }
        nextAuthRenewalMs = elapsedMs + 4 * 60 * 1000;
      }
      const pollUrl = `${url}?_ts=${Date.now()}_${i}`;
      // Broker task status is intentionally public: it does not require a new
      // Basic-auth request for each poll, and Celery remains unaffected.
      const response = await fetch(pollUrl, {
        method: 'GET',
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      });

      let data = {};
      try {
        data = await response.json();
      } catch (parseError) {
        return { status: response.status || 500, message: 'The background task returned an invalid response.' };
      }
      if (data.state === 'SUCCESS') {
        return data.data ?? { status: 500, message: 'The background task completed without returning a result.' };
      }
      if (data.state === 'FAILURE') {
        return { status: response.status >= 400 ? response.status : 500,
          message: data.error || 'The background task failed.' };
      }
      if (['PENDING', 'STARTED', 'RETRY', 'RECEIVED'].includes(data.state)) continue;
      if (!response.ok) {
        return { status: response.status || 500,
          message: data.error || data.message || 'The background task failed.' };
      }
      if (data.data !== undefined) return data.data;
    }

    const elapsedMinutes = Math.max(1, Math.round(elapsedMs / 60000));
    return { status: 504, taskStillRunning: true, taskName: task_data.task_name,
      taskId: task_data.task_id,
      message: `The background refresh has not finished after ${elapsedMinutes} minutes. It may still be running in Celery, so do not start another refresh yet.` };
  } catch (error) {
    console.error(error);
    return { status: 500, message: error?.message || String(error) || 'The background task failed.' };
  }
}
